import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const MyOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });

  // Get userId from sessionStorage
  const userId = sessionStorage.getItem('userid');

  const fetchOrders = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token not found. Please log in again.');
      }

      const response = await axios.get(`${API_URL}/api/orders/user`, {
        params: { userId, page, limit: 10 },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Handle different API response formats
      let ordersData;
      let paginationData = { currentPage: 1, totalPages: 1, totalItems: 0 };
      
      if (Array.isArray(response.data)) {
        // If API returns a simple array of orders
        ordersData = response.data;
      } else if (response.data.orders) {
        // If API returns a structured response with pagination
        ordersData = response.data.orders;
        paginationData = {
          currentPage: response.data.currentPage || 1,
          totalPages: response.data.totalPages || 1,
          totalItems: response.data.totalItems || ordersData.length
        };
      } else {
        // Fallback if structure is unknown
        ordersData = response.data;
      }
      
      setOrders(ordersData);
      setPagination(paginationData);
      setError('');
    } catch (err) {
      console.error('Error fetching orders:', err.message);
      setError(err.response?.data?.error || 'Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchOrders();
    } else {
      setError('User not logged in');
      setLoading(false);
    }
  }, [userId]);

  const handlePageChange = (newPage) => {
    fetchOrders(newPage);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'PAID':
        return 'bg-green-100 text-green-700';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-700';
      case 'FAILED':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Helper function to display product names in a compact format
  const getProductSummary = (products) => {
    if (!products || products.length === 0) return "No products";
    
    // Get the first two product names
    const firstProducts = products.slice(0, 2).map(p => p.productName).filter(Boolean);
    
    // If there are more than 2 products, add "and X more"
    if (products.length > 2) {
      return `${firstProducts.join(", ")} and ${products.length - 2} more`;
    }
    
    return firstProducts.join(", ");
  };

  if (loading && orders.length === 0) {
    return (
      <div className="p-6 mt-12 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <>
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white w-full max-w-2xl p-6 rounded-lg shadow-lg relative max-h-[90vh] overflow-y-auto">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-red-600 text-xl font-bold"
              onClick={() => setSelectedOrder(null)}
            >
              &times;
            </button>
            <h2 className="text-2xl font-bold mb-2">Order #{selectedOrder.orderId || selectedOrder._id}</h2>
            <p className="text-sm text-gray-500 mb-4">
              Placed on {new Date(selectedOrder.createdAt).toLocaleString()}
            </p>
            <div className="flex justify-between items-center mb-4">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusClass(selectedOrder.status)}`}
              >
                {selectedOrder.status}
              </span>
              <span className="text-lg font-semibold text-green-600">
                ₹{selectedOrder.totalAmount?.toFixed(2) || selectedOrder.totalAmount}
              </span>
            </div>
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">Items</h3>
              {selectedOrder.products?.length > 0 ? (
                <div className="bg-gray-50 rounded-lg p-3">
                  <table className="w-full">
                    <thead>
                      <tr className="text-sm text-gray-600">
                        <th className="text-left pb-2">Item</th>
                        <th className="text-center pb-2">Qty</th>
                        <th className="text-right pb-2">Price</th>
                        <th className="text-right pb-2">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.products && selectedOrder.products.map((product, index) => (
                        <tr key={index} className="border-t border-gray-200">
                          <td className="py-2 text-gray-800">{product.productName}</td>
                          <td className="py-2 text-center">{product.quantity}</td>
                          <td className="py-2 text-right">₹{product.price}</td>
                          <td className="py-2 text-right font-medium">₹{(product.quantity * product.price).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500">No item details available</p>
              )}
            </div>
            {selectedOrder.address && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">Delivery Address</h3>
                <p className="text-sm"><strong>Name:</strong> {selectedOrder.address.fullName}</p>
                <p className="text-sm"><strong>Phone:</strong> {selectedOrder.address.phoneNumber}</p>
                <p className="text-sm"><strong>Address:</strong> {selectedOrder.address.addressLine}</p>
                <p className="text-sm"><strong>Landmark:</strong> {selectedOrder.address.landmark}</p>
                <p className="text-sm"><strong>Pincode:</strong> {selectedOrder.address.pincode}</p>
              </div>
            )}
            <div className="text-sm text-gray-500 mt-4 border-t pt-3">
              {selectedOrder.paymentId && <p><strong>Payment ID:</strong> {selectedOrder.paymentId}</p>}
              <p><strong>Order ID:</strong> {selectedOrder.orderId || selectedOrder._id}</p>
              <p><strong>Updated At:</strong> {new Date(selectedOrder.updatedAt || selectedOrder.createdAt).toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      <div className="p-6 max-w-6xl mx-auto mt-8">
        <h2 className="text-3xl font-bold mb-6 text-gray-800 text-center">
          My Orders
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="overflow-x-auto bg-white shadow-lg rounded-lg">
          <table className="min-w-full border border-gray-300">
            <thead className="bg-gray-100">
  <tr>
    <th className="px-4 py-3 border-b text-left text-sm font-semibold text-gray-700">Items</th>
    <th className="px-4 py-3 border-b text-center text-sm font-semibold text-gray-700">Total Amount</th>
    <th className="px-4 py-3 border-b text-center text-sm font-semibold text-gray-700">Status</th>
    <th className="px-4 py-3 border-b text-center text-sm font-semibold text-gray-700">Order Date</th>
    <th className="px-4 py-3 border-b text-center text-sm font-semibold text-gray-700">Actions</th>
  </tr>
</thead>
<tbody>
  {orders.length > 0 ? (
    orders.map((order) => (
      <tr key={order._id} className="hover:bg-gray-50 transition duration-150">
        <td className="px-4 py-3 border-b text-gray-800">
          {order.products && order.products.length > 0 ? (
            <div>
              <p className="font-medium">{order.products.length} {order.products.length === 1 ? 'item' : 'items'}</p>
              <p className="text-sm text-gray-600 truncate max-w-xs">
                {getProductSummary(order.products)}
              </p>
            </div>
          ) : (
            <span className="text-gray-500">No details available</span>
          )}
        </td>
        <td className="px-4 py-3 border-b text-center text-gray-800">
          ₹{order.totalAmount?.toFixed(2) || order.totalAmount}
        </td>
        <td className="px-4 py-3 border-b text-center">
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusClass(order.status)}`}
          >
            {order.status}
          </span>
        </td>
        <td className="px-4 py-3 border-b text-center text-gray-800">
          {new Date(order.createdAt).toLocaleString()}
        </td>
        <td className="px-4 py-3 border-b text-center">
          <button
             onClick={() => setSelectedOrder(order)}
            className="text-blue-600 hover:underline text-sm font-medium"
          >
            View Details
          </button>
        </td>
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan="5" className="px-4 py-3 text-center text-gray-500">
        No orders found.
      </td>
    </tr>
  )}
</tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className="px-4 py-2 mx-1 bg-gray-200 text-gray-700 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-4 py-2 mx-1 text-gray-700">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className="px-4 py-2 mx-1 bg-gray-200 text-gray-700 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default MyOrdersPage;